import { Panel, Typography } from "@/components/atoms";
import { PageList } from "@/components/molecules";
import { usePermissions } from "@/permissions";
import { EPermission } from "@/permissions/types";
import { AccessControl } from "./AccessControl/AccessControl";
import { BusinessDetails } from "./BusinessDetails/BusinessDetails";
import { DisplayOptions } from "./DisplayOptions/DisplayOptions";
import { EmailNotifications } from "./EmailNotifications/EmailNotifications";
import { SMSVoiceNotifications } from "./SMSVoiceNotifications/SMSVoiceNotifications";
import { WebsiteDetails } from "./WebsiteDetails/WebsiteDetails";

type Subpage = {
  name: string;
  desc: string;
  to: string;
  icon: string;
  permission?: EPermission | EPermission[];
};

const subpages: Subpage[] = [
  {
    name: "Business Details",
    desc: "Your public company information.",
    to: "business-details",
    icon: "Buildings",
    permission: EPermission.ProviderConfigBusinessDetailsManage,
  },
  {
    name: "Website Details",
    desc: "URLs for your website, google reviews and important links.",
    to: "website-details",
    icon: "Global",
    permission: EPermission.ProviderConfigWebsiteDetailsManage,
  },
  {
    name: "Email Notifications",
    desc: "Email notification settings and hotline phone numbers.",
    to: "email-notifications",
    icon: "Notification",
    permission: EPermission.ProviderConfigEmailNotificationsManage,
  },
  {
    name: "SMS / Voice Notifications",
    desc: "SMS and Voice notification settings.",
    to: "sms-voice-notifications",
    icon: "MessageNotif",
    permission: EPermission.ProviderConfigSmsVoiceNotificationsManage,
  },
  {
    name: "Access Control",
    desc: "Controls enabling / disabling site features.",
    to: "access-control",
    icon: "Setting3",
    permission: [EPermission.ProviderConfigProfileAdvancedManage, EPermission.ProviderConfigAccessControlManage],
  },
  {
    name: "Display Options",
    desc: "Configure what your users can see.",
    to: "display-options",
    icon: "Eye",
    permission: EPermission.ProviderConfigDisplayOptionsManage,
  },
];

export const Business = () => {
  const { hasPermission, hasAllPermissions } = usePermissions();

  const visibleSubpages = subpages.filter(({ permission }) => {
    if (!permission) return true;
    return Array.isArray(permission) ? hasAllPermissions(permission) : hasPermission(permission);
  });

  return (
    <Panel>
      <Typography variant="h3">Business Settings</Typography>
      <PageList className="-mx-5 mt-2">
        {visibleSubpages.map(({ name, desc, to, icon }) => (
          <PageList.Item key={name} title={name} description={desc} to={to} icon={icon} />
        ))}
      </PageList>
    </Panel>
  );
};

Business.BusinessDetails = BusinessDetails;
Business.WebsiteDetails = WebsiteDetails;
Business.EmailNotifications = EmailNotifications;
Business.SMSVoiceNotifications = SMSVoiceNotifications;
Business.AccessControl = AccessControl;
Business.DisplayOptions = DisplayOptions;
